
// Environment-specific Sentry configuration
export const getSentryConfig = () => {
  const environment = import.meta.env.MODE
  const isDevelopment = environment === 'development'
  const isProduction = environment === 'production'
  
  return {
    dsn: import.meta.env.VITE_SENTRY_DSN || "https://0ccf5c9fd86fb9935d5d54b48c222a37@o4507194182991872.ingest.us.sentry.io/4509753492439040",
    environment,
    
    // Enable debug mode in development
    debug: isDevelopment,
    
    // Send default personally identifiable information
    sendDefaultPii: true,
    
    // Performance Monitoring
    tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in production, 100% in development
    
    // Profiling - requires tracing to be enabled
    profilesSampleRate: isProduction ? 0.1 : 1.0, // Profile 10% of transactions in production
    
    // Session Replay
    replaysSessionSampleRate: isProduction ? 0.1 : 1.0, // 10% in production, 100% in development
    replaysOnErrorSampleRate: 1.0, // Always record on errors
    
    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',
    
    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      /extension\//,
      /^chrome:\/\//,
      /^moz-extension:\/\//,
      
      // Network errors that are likely user connection issues
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      
      // Ignore specific third-party errors
      'grecaptcha is not defined',
      'fb_xd_fragment',
    ],
    
    // Filter transactions
    tracesSampler: (samplingContext: any) => {
      // Check if transactionContext exists
      if (!samplingContext?.transactionContext?.name) {
        // Default sampling for undefined transactions
        return isProduction ? 0.1 : 1.0
      }
      
      const transactionName = samplingContext.transactionContext.name
      
      // Don't sample health check endpoints
      if (transactionName === 'GET /health') {
        return 0
      }
      
      // Higher sampling for error-prone endpoints
      if (transactionName.includes('timesheets')) {
        return isProduction ? 0.5 : 1.0
      }
      
      // Default sampling
      return isProduction ? 0.1 : 1.0
    },
    
    // Before send callback for filtering
    beforeSend(event: any, hint: any) {
      // Don't send events if there's a network error with Sentry itself
      if (hint?.originalException?.message?.includes('Failed to fetch') && 
          hint?.originalException?.stack?.includes('sentry')) {
        return null
      }
      // Filter out events from browser extensions
      if (event.exception && event.exception.values?.[0]?.stacktrace?.frames) {
        const frames = event.exception.values[0].stacktrace.frames;
        if (frames.some((frame: any) => frame.filename?.includes('extension://'))) {
          return null;
        }
      }
      
      // Don't send events in development if they're just console logs
      if (isDevelopment && event.level === 'log') {
        return null
      }
      
      // Add user context from localStorage if available
      const userStr = localStorage.getItem('user')
      if (userStr) {
        try {
          const user = JSON.parse(userStr)
          event.user = {
            id: user.id,
            email: user.email,
            username: user.name,
            ...event.user
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      return event;
    },
    
    // Transport options
    transportOptions: {
      // Retry failed requests
      fetchOptions: {
        keepalive: true,
      },
    },
    
    // Limit retries and add timeout
    maxRetries: isDevelopment ? 0 : 2,
    timeout: 5000,
    
    // Disable native integration that might cause issues
    autoSessionTracking: isProduction,
  }
}

// Helper to check if Sentry should be enabled
export const shouldEnableSentry = () => {
  // Disable in test environments
  if (import.meta.env.MODE === 'test') {
    return false
  }
  
  // Check if DSN is configured
  const dsn = import.meta.env.VITE_SENTRY_DSN || "https://0ccf5c9fd86fb9935d5d54b48c222a37@o4507194182991872.ingest.us.sentry.io/4509753492439040"
  if (!dsn || dsn === 'YOUR_SENTRY_DSN_HERE') {
    return false
  }
  
  // Optionally disable in development to avoid noise
  if (import.meta.env.MODE === 'development' && import.meta.env.VITE_DISABLE_SENTRY === 'true') {
    return false
  }
  
  // Disable if explicitly set
  if (import.meta.env.VITE_DISABLE_SENTRY === 'true') {
    return false
  }
  
  // Enable in all other cases
  return true
}

// Helper to get user-friendly error messages
export const getUserFriendlyErrorMessage = (error: Error): string => {
  const errorMap: Record<string, string> = {
    'NetworkError': 'Network connection issue. Please check your internet connection.',
    'Failed to fetch': 'Unable to connect to the server. Please try again later.',
    '401': 'Your session has expired. Please log in again.',
    '403': 'You do not have permission to perform this action.',
    '404': 'The requested resource was not found.',
    '429': 'Too many requests. Please slow down and try again.',
    '500': 'Server error. Please try again later.',
    '503': 'Service temporarily unavailable. Please try again later.',
  }
  
  // Check error message for known patterns
  for (const [pattern, message] of Object.entries(errorMap)) {
    if (error.message.includes(pattern) || error.name.includes(pattern)) {
      return message
    }
  }
  
  // Default message
  return 'An unexpected error occurred. Please try again.'
}